export type PreloadImagesOptions = {
  concurrency?: number;
  onProgress?: (loaded: number, total: number) => void;
};

export type PreloadImagesResult = {
  loaded: number;
  total: number;
  failedUrls: string[];
};

const preloadedUrls = new Set<string>();

export function isUrlPreloaded(url: string): boolean {
  return preloadedUrls.has(url);
}

export function markUrlPreloaded(url: string): void {
  if (url !== '') {
    preloadedUrls.add(url);
  }
}

/** Test helper — clears the in-memory session registry. */
export function clearPreloadedUrlRegistry(): void {
  preloadedUrls.clear();
}

function dedupeUrls(urls: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const url of urls) {
    if (url === '' || seen.has(url)) {
      continue;
    }
    seen.add(url);
    result.push(url);
  }
  return result;
}

export function preloadImage(url: string): Promise<void> {
  if (preloadedUrls.has(url)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      markUrlPreloaded(url);
      resolve();
    };
    img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
    img.src = url;
  });
}

export async function preloadImages(
  urls: readonly string[],
  options: PreloadImagesOptions = {},
): Promise<PreloadImagesResult> {
  const concurrency = Math.max(1, options.concurrency ?? 6);
  const unique = dedupeUrls(urls);
  const total = unique.length;
  const pending = unique.filter((url) => !preloadedUrls.has(url));
  let loaded = total - pending.length;
  const failedUrls: string[] = [];

  const reportProgress = () => {
    options.onProgress?.(loaded, total);
  };

  reportProgress();

  if (pending.length === 0) {
    return { loaded, total, failedUrls: [] };
  }

  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < pending.length) {
      const index = nextIndex;
      nextIndex += 1;
      const url = pending[index];
      try {
        await preloadImage(url);
      } catch {
        failedUrls.push(url);
        console.warn('[assets] image preload failed', url);
      }
      loaded += 1;
      reportProgress();
    }
  }

  const workerCount = Math.min(concurrency, pending.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return { loaded, total, failedUrls };
}
